/**
 * Presentation of the five attendance states.
 *
 * A status colour never carries meaning on its own here: every appearance pairs a colour with
 * a glyph and a word, and the word is always available to assistive technology even when the
 * layout only has room for the glyph.
 */

import type { ReactNode } from 'react';

import { countOf, type StateCounts } from '../../shared/stats.js';
import { ATTENDANCE_STATES, AttendanceState, type DisplayState } from '../../shared/types.js';
import { CheckIcon, ClockIcon, CrossIcon, NoteIcon, type IconProps } from './icons.js';

/** How one state is drawn and named. */
export interface StateAppearance {
  state: DisplayState;
  /** Word shown beside the glyph and used as the accessible name. */
  label: string;
  /** Shorter word for a column of figures, where five labels share one line. */
  short: string;
  /** Glyph component; `null` for the unset state, which is drawn as an empty outline. */
  Icon: ((props: IconProps) => ReactNode) | null;
  /** Classes for the selected, filled form. */
  solid: string;
  /** Classes for the tinted, unselected form. */
  soft: string;
  /** Class that paints text in the state's colour. */
  text: string;
  /** CSS custom property holding the state's colour, for SVG fills. */
  cssVar: string;
}

const APPEARANCES: Record<DisplayState, StateAppearance> = {
  [AttendanceState.Attending]: {
    state: AttendanceState.Attending,
    label: 'Attending',
    short: 'Attending',
    Icon: CheckIcon,
    solid: 'bg-attending text-white border-attending',
    soft: 'bg-attending-soft text-attending-text border-transparent',
    text: 'text-attending-text',
    cssVar: 'var(--color-attending)',
  },
  [AttendanceState.Late]: {
    state: AttendanceState.Late,
    label: 'Late',
    short: 'Late',
    Icon: ClockIcon,
    solid: 'bg-late text-white border-late',
    soft: 'bg-late-soft text-late-text border-transparent',
    text: 'text-late-text',
    cssVar: 'var(--color-late)',
  },
  [AttendanceState.Excused]: {
    state: AttendanceState.Excused,
    label: 'Excused',
    short: 'Excused',
    Icon: NoteIcon,
    solid: 'bg-excused text-[#3a2a02] border-excused',
    soft: 'bg-excused-soft text-excused-text border-transparent',
    text: 'text-excused-text',
    cssVar: 'var(--color-excused)',
  },
  [AttendanceState.Missing]: {
    state: AttendanceState.Missing,
    label: 'Missing',
    short: 'Missing',
    Icon: CrossIcon,
    solid: 'bg-missing text-white border-missing',
    soft: 'bg-missing-soft text-missing-text border-transparent',
    text: 'text-missing-text',
    cssVar: 'var(--color-missing)',
  },
  unset: {
    state: 'unset',
    label: 'Not recorded',
    short: 'Not set',
    Icon: null,
    solid: 'bg-unset text-white border-unset',
    soft: 'bg-unset-soft text-ink-3 border-transparent',
    text: 'text-ink-3',
    cssVar: 'var(--color-unset)',
  },
};

/** Returns how `state` is drawn and named. */
export function appearanceOf(state: DisplayState): StateAppearance {
  return APPEARANCES[state];
}

/** The four recordable states, in display order. */
export const RECORDABLE_APPEARANCES: readonly StateAppearance[] = ATTENDANCE_STATES.map(appearanceOf);

/** Every state including "not recorded", for legends and distribution bars. */
export const ALL_APPEARANCES: readonly StateAppearance[] = [...RECORDABLE_APPEARANCES, APPEARANCES.unset];

/**
 * One button of the roll-call control, and the states repeated presses walk through.
 *
 * The four states are two answers to one question — someone was there, or they were not —
 * and the shade of each answer is a detail of it. Pairing them this way halves the number of
 * targets on a roster row and puts the two answers far enough apart that a wrong tap is a
 * wrong shade rather than the opposite verdict.
 */
export interface StateCycle {
  /** Name of the answer this button gives, used as the first word of its accessible name. */
  label: string;
  /** States in press order; after the last one, the next press clears the mark. */
  states: readonly AttendanceState[];
  /**
   * Why this button cannot be pressed, or `undefined` while it can.
   *
   * A button that is present but disabled explains itself; a button that is simply absent
   * needs a sentence next to the roster telling everyone why, on every session, forever.
   */
  disabledReason?: string;
}

/** Someone was there: first press records attendance, the next downgrades it to late. */
export const PRESENT_CYCLE: StateCycle = {
  label: 'Present',
  states: [AttendanceState.Attending, AttendanceState.Late],
};

/** Someone was not there: first press records the absence, the next excuses it. */
export const ABSENT_CYCLE: StateCycle = {
  label: 'Absent',
  states: [AttendanceState.Missing, AttendanceState.Excused],
};

/** The two buttons offered for a session that has already happened. */
export const RECORDING_CYCLES: readonly StateCycle[] = [PRESENT_CYCLE, ABSENT_CYCLE];

/**
 * The buttons offered for a session still to come.
 *
 * Attendance cannot be observed before it happens, but an absence announced in advance can be,
 * so the only reachable state is "excused". The present button still appears, disabled, rather
 * than vanishing: the greyed control is the explanation.
 */
export const FUTURE_CYCLES: readonly StateCycle[] = [
  {
    ...PRESENT_CYCLE,
    disabledReason: 'This session has not happened yet',
  },
  { label: 'Absent', states: [AttendanceState.Excused] },
];

/**
 * Returns the state one press of `cycle` moves to from `current`.
 *
 * Pressing a button whose cycle does not hold the current state jumps to that cycle's first
 * state, so switching verdict is always one press. Pressing past the last state clears the
 * mark, which is what `null` means.
 */
export function nextInCycle(cycle: StateCycle, current: AttendanceState | undefined): AttendanceState | null {
  const position = current === undefined ? -1 : cycle.states.indexOf(current);
  if (position === -1) return cycle.states[0] ?? null;
  return cycle.states[position + 1] ?? null;
}

/** The state `cycle` currently holds, or `undefined` when the mark is elsewhere or unset. */
function heldBy(cycle: StateCycle, current: AttendanceState | undefined): AttendanceState | undefined {
  return current !== undefined && cycle.states.includes(current) ? current : undefined;
}

/** Describes what one more press of `cycle` does, for the button's accessible name. */
function pressHint(cycle: StateCycle, current: AttendanceState | undefined): string {
  const next = nextInCycle(cycle, current);
  return next === null ? 'Press to clear it.' : `Press to mark ${APPEARANCES[next].label.toLowerCase()}.`;
}

/**
 * The five states as a row of colour chips and words.
 *
 * Used where a distribution bar appears without figures beside it — a roster of them, one per
 * person — so the colours are still named on screen rather than only in a tooltip.
 */
export function StateLegend(): ReactNode {
  return (
    <ul className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
      {ALL_APPEARANCES.map((appearance) => (
        <li key={appearance.state} className="inline-flex items-center gap-1.5 text-tiny text-ink-2">
          <span
            aria-hidden="true"
            className="inline-block h-2 w-3.5 rounded-full"
            style={{ backgroundColor: appearance.cssVar }}
          />
          {appearance.short}
        </li>
      ))}
    </ul>
  );
}

/**
 * How many slots sit in each state, as five directly labelled figures.
 *
 * The bar shows the proportions; this is the count behind each one, because "how many are
 * still not set" is a number to act on rather than a share to compare.
 *
 * The colour lives in the figure itself rather than in a rule above it. A row of rules under
 * a stacked bar reads as a second, broken copy of that bar — its five equal columns will not
 * line up with the bar's proportional segments, and the eye tries to make them. Colouring the
 * number ties each figure to its segment by identity instead of by position, and the word
 * below it means the colour never carries the meaning alone.
 *
 * The pairing is a description list: the word names the state, the figure answers it. The DOM
 * keeps that order for assistive technology while the column reverses it on screen, so the
 * numbers sit on one baseline and read first.
 */
export function StateFigures({ counts }: { counts: StateCounts }): ReactNode {
  return (
    <dl className="grid grid-cols-5 gap-x-2 sm:gap-x-4">
      {ALL_APPEARANCES.map((appearance) => (
        <div key={appearance.state} className="flex flex-col-reverse gap-0.5">
          <dt className="text-tiny leading-tight text-ink-2">{appearance.short}</dt>
          <dd className={`tabular ml-0 text-title leading-none font-bold ${appearance.text}`}>
            {countOf(counts, appearance.state)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The attendance control, shared by the roll call and a person's history.
 *
 * Each button owns one verdict and cycles through its shades: unset, missing, excused, unset
 * again. The two sit in one outline with a hairline between them, because they are one
 * question with two answers rather than two independent switches. The selected button takes
 * its state's fill and shows that state's glyph, so what is recorded is readable from the
 * button that recorded it.
 */
export function StateChoice({
  groupLabel,
  cycles,
  current,
  onSet,
}: {
  /** Accessible name of the group, naming what is being marked. */
  groupLabel: string;
  /** Buttons offered; a future session offers only the excused half of the absent one. */
  cycles: readonly StateCycle[];
  current: AttendanceState | undefined;
  /** Called with the state the press lands on, or `null` when it clears the mark. */
  onSet: (next: AttendanceState | null) => void;
}): ReactNode {
  return (
    <div
      role="group"
      aria-label={groupLabel}
      className="flex shrink-0 divide-x divide-line-strong overflow-hidden rounded-control
        border border-line-strong bg-surface"
    >
      {cycles.map((cycle) => {
        const held = heldBy(cycle, current);
        const appearance = APPEARANCES[held ?? cycle.states[0]];
        const { Icon } = appearance;
        const blocked = cycle.disabledReason !== undefined;
        return (
          <button
            key={cycle.label}
            type="button"
            disabled={blocked}
            aria-pressed={held !== undefined}
            aria-label={blocked
              ? `${cycle.label}: unavailable. ${cycle.disabledReason}.`
              : `${cycle.label}: ${held === undefined ? 'not set' : appearance.label.toLowerCase()}.`
                + ` ${pressHint(cycle, current)}`}
            title={blocked ? cycle.disabledReason : held === undefined ? cycle.label : appearance.label}
            onClick={() => onSet(nextInCycle(cycle, current))}
            className={`inline-flex h-11 w-14 items-center justify-center transition-colors sm:w-16
              ${blocked
                ? 'cursor-not-allowed bg-sunken text-ink-3'
                : held === undefined ? 'text-ink-2 hover:bg-sunken hover:text-ink' : appearance.solid}`}
          >
            {Icon === null ? null : <Icon size={20} />}
          </button>
        );
      })}
      <span className="sr-only">{appearanceOf(current ?? 'unset').label}</span>
    </div>
  );
}
